import { Component, createRef } from "react";
import { KEALink, KEAPaper, keaNamespace, KEAPaperBuilder, PaperEventTypes } from "@kea-mod/jointjs";
import * as joint from "jointjs";

import { KEAGraphContext } from "context/KEAGraphContext";
import { getZoomDisabled } from "helper/Helper";

const paperOptions: joint.dia.Paper.Options = {
  width: "100%",
  height: "100%",
  gridSize: 8,
  cellViewNamespace: keaNamespace,
  drawGrid: {
    name: "dot",
    args: [
      {
        color: "#1a1a1a",
        thickness: 0.6,
      },
    ],
  },
  defaultConnectionPoint: { name: "boundary" },
  linkPinning: true,
  validateConnection: function () {
    return true;
  },
  defaultAnchor: {
    name: "modelCenter",
    args: {
      rotate: true,
      padding: 20,
    },
  },
  background: {
    color: "#D7EADC",
  },
  interactive: false,
};

interface State {}

interface Props {
  width: number;
  height: number;
  setNode?: (node?: joint.dia.Element) => void;
  pushPresent?: () => void;
}

export class PreviewGraph extends Component<Props, State> {
  static defaultProps = {
    pushPresent: () => {},
  };

  static contextType = KEAGraphContext;
  context!: React.ContextType<typeof KEAGraphContext>;

  private canvasContainerRef: React.RefObject<HTMLDivElement>;
  private pendingHighlightIds: string[] | null = null;
  private pendingFeedbackHighlights: Record<string, string> | null = null;
  private pendingPairHighlightIds: string[] | null = null;
  private lastModelLoaded = false;
  private readonly hoverHighlightColor = "#F1A63A";
  private readonly negativeColor = "#D64545";
  private readonly warningColor = "#9C9998";
  private readonly positiveColor = "#3FCE41";
  private readonly pairHighlightColor = "#8b63d9";
  private readonly pairLinkHighlightColor = "#8b63d9";
  private onMouseEnter?: (cellView: joint.dia.CellView) => void;
  private onMouseLeave?: () => void;
  private onHoverEnter?: (cellView: joint.dia.CellView) => void;
  private onHoverLeave?: () => void;
  private onLinkHoverEnter?: (cellView: joint.dia.CellView) => void;
  private onLinkHoverLeave?: () => void;

  constructor(props: Props) {
    super(props);
    this.canvasContainerRef = createRef();
  }

  componentDidMount = () => {
    //this is a hacky, but works. Joint elements need to be in DOM to import new graphs. Otherwise, the scaling goes wrong. There is no way to guarantee, that the virtualDOM is indeed present in the actual DOM.
    //look at https://stackoverflow.com/questions/26556436/react-after-render-code
    setTimeout(() => {
      this.context.setModelingPaper(this.initializePaper());
    }, 1000);
    this.lastModelLoaded = this.context.modelLoaded;
    window.addEventListener("message", this.handleMessage);
  };

  componentWillUnmount = () => {
    this.context.modelingPaper.removeDragPaperEvent();
    if (this.onMouseEnter) {
      this.context.modelingPaper.off("cell:mouseenter", this.onMouseEnter);
    }
    if (this.onMouseLeave) {
      this.context.modelingPaper.off("cell:mouseleave", this.onMouseLeave);
    }
    if (this.onHoverEnter) {
      this.context.modelingPaper.off("cell:mouseenter", this.onHoverEnter);
    }
    if (this.onHoverLeave) {
      this.context.modelingPaper.off("cell:mouseleave", this.onHoverLeave);
    }
    if (this.onLinkHoverEnter) {
      this.context.modelingPaper.off("link:mouseenter", this.onLinkHoverEnter);
    }
    if (this.onLinkHoverLeave) {
      this.context.modelingPaper.off("link:mouseleave", this.onLinkHoverLeave);
    }
    window.removeEventListener("message", this.handleMessage);
  };

  componentDidUpdate = () => {
    if (this.context.modelLoaded && !this.lastModelLoaded) {
      this.lastModelLoaded = true;
      this.applyHighlightsIfReady();
    } else if (!this.context.modelLoaded && this.lastModelLoaded) {
      this.lastModelLoaded = false;
    }
  };

  handleMessage = (event: MessageEvent) => {
    const data = event.data as { type?: string; ids?: unknown; feedback?: unknown };
    if (data?.type === "applyPairHighlights") {
      const ids = Array.isArray(data.ids) ? data.ids.filter((id) => typeof id === "string") : null;
      this.pendingPairHighlightIds = ids ? (ids as string[]) : null;
      this.applyHighlightsIfReady();
      return;
    }
    if (!data || data.type !== "applyFeedbackHighlights") {
      if (!data || data.type !== "applyFeedbackMessages") {
        return;
      }
      this.pendingFeedbackHighlights = this.buildFeedbackHighlightMap(data.feedback);
      this.applyHighlightsIfReady();
      return;
    }
    const ids = Array.isArray(data.ids) ? data.ids.filter((id) => typeof id === "string") : null;
    this.pendingHighlightIds = ids ? (ids as string[]) : null;
    this.applyHighlightsIfReady();
  };

  getHighlightMarkup = (color: string) => ({
    stroke: color,
    "stroke-width": 4,
  });

  getLinkHighlightMarkup = (color: string) => ({
    stroke: color,
    "stroke-width": 3,
    "stroke-linecap": "round",
    "stroke-linejoin": "round",
  });

  highlightElement = (id: string, color: string) => {
    const cell = this.context.modelingGraph.getCell(id);
    if (!cell) {
      return;
    }
    const cellView = this.context.modelingPaper.findViewByModel(cell);
    if (!cellView) {
      return;
    }
    if (cell.get("type") === "kea.Link") {
      joint.highlighters.stroke.add(cellView as joint.dia.LinkView, "line", "link-outline", {
        attrs: this.getLinkHighlightMarkup(color),
      });
    } else {
      joint.highlighters.mask.add(cellView as any, "body", "highlighter-selector", {
        attrs: this.getHighlightMarkup(color),
      });
    }
  };

  highlightPairElement = (id: string) => {
    const cell = this.context.modelingGraph.getCell(id);
    if (!cell) {
      return;
    }
    const color = cell.get("type") === "kea.Link" ? this.pairLinkHighlightColor : this.pairHighlightColor;
    this.highlightElement(id, color);
  };

  unhighlightAllElements = () => {
    const cells = this.context.modelingGraph.getCells();
    cells.forEach((cell) => {
      if (cell.get("type") === "kea.Link") {
        (cell as KEALink).unhighlight();
        const cellView = this.context.modelingPaper.findViewByModel(cell);
        if (cellView) {
          joint.dia.HighlighterView.remove(cellView as joint.dia.LinkView);
        }
      } else {
        const cellView = this.context.modelingPaper.findViewByModel(cell);
        if (!cellView) {
          return;
        }
        const elementView: joint.dia.ElementView = cellView as unknown as joint.dia.ElementView;
        joint.dia.HighlighterView.remove(elementView);
      }
    });
  };

  applyHighlightsIfReady = () => {
    if (!this.context.modelLoaded) {
      return;
    }
    this.unhighlightAllElements();
    if (this.pendingHighlightIds !== null) {
      this.pendingHighlightIds.forEach((id) => this.highlightElement(id, this.hoverHighlightColor));
      return;
    }
    if (this.pendingFeedbackHighlights && Object.keys(this.pendingFeedbackHighlights).length > 0) {
      Object.entries(this.pendingFeedbackHighlights).forEach(([id, color]) => this.highlightElement(id, color));
    }
    if (this.pendingPairHighlightIds && this.pendingPairHighlightIds.length > 0) {
      this.pendingPairHighlightIds.forEach((id) => this.highlightPairElement(id));
    }
  };

  buildFeedbackHighlightMap = (feedback: unknown): Record<string, string> => {
    if (!Array.isArray(feedback)) {
      return {};
    }
    const highlightMap: Record<string, number> = {};
    feedback.forEach((entry) => {
      if (!entry || typeof entry !== "object") {
        return;
      }
      const elementId = typeof (entry as { elementId?: unknown }).elementId === "string" ? (entry as any).elementId : "";
      if (!elementId) {
        return;
      }
      const typeValue = typeof (entry as { type?: unknown }).type === "string" ? (entry as any).type : "";
      let weight = 2;
      if (typeValue === "VeryNegative" || typeValue === "Negative" || typeValue === "Fatal") {
        weight = 3;
      } else if (typeValue === "Good" || typeValue === "VeryGood") {
        weight = 1;
      } else if (typeValue === "Neutral") {
        weight = 2;
      }
      const current = highlightMap[elementId] ?? 0;
      highlightMap[elementId] = Math.max(current, weight);
    });
    const colors: Record<string, string> = {};
    Object.entries(highlightMap).forEach(([id, weight]) => {
      if (weight >= 3) {
        colors[id] = this.negativeColor;
      } else if (weight === 2) {
        colors[id] = this.warningColor;
      } else {
        colors[id] = this.positiveColor;
      }
    });
    return colors;
  };

  initializePaper = (): KEAPaper => {
    const paperBuilder = new KEAPaperBuilder(this.canvasContainerRef, this.context.modelingGraph, paperOptions).event(
      PaperEventTypes.PAPER_DRAG,
    );

    if (!getZoomDisabled()) {
      paperBuilder.event(PaperEventTypes.PAPER_ZOOM);
    }

    const paper = paperBuilder.build();
    if (this.props.setNode) {
      this.onMouseEnter = (cellView) => {
        this.props.setNode?.(cellView.model as joint.dia.Element);
      };
      this.onMouseLeave = () => {
        this.props.setNode?.();
      };
      paper.on("cell:mouseenter", this.onMouseEnter);
      paper.on("cell:mouseleave", this.onMouseLeave);
    }
    this.onHoverEnter = (cellView) => {
      if (cellView.model.get("type") === "kea.Link") {
        return;
      }
      window.parent.postMessage(
        {
          type: "previewHover",
          elementId: cellView.model.id,
        },
        "*",
      );
    };
    this.onHoverLeave = () => {
      window.parent.postMessage(
        {
          type: "previewHover",
          elementId: null,
        },
        "*",
      );
    };
    this.onLinkHoverEnter = (cellView) => {
      window.parent.postMessage(
        {
          type: "previewHover",
          elementId: cellView.model.id,
        },
        "*",
      );
    };
    this.onLinkHoverLeave = () => {
      window.parent.postMessage(
        {
          type: "previewHover",
          elementId: null,
        },
        "*",
      );
    };
    paper.on("cell:mouseenter", this.onHoverEnter);
    paper.on("cell:mouseleave", this.onHoverLeave);
    paper.on("link:mouseenter", this.onLinkHoverEnter);
    paper.on("link:mouseleave", this.onLinkHoverLeave);
    return paper;
  };

  historyClb = () => {
    this.props.pushPresent?.();
  };

  render() {
    return (
      <div
        style={{ width: this.props.width, height: this.props.height, backgroundColor: "#D7EADC" }}
        className="box m-0 p-0"
      >
        <div id="canvas" ref={this.canvasContainerRef}></div>
      </div>
    );
  }
}
